import { Component, OnInit, OnDestroy } from "@angular/core";
import { Wagon } from "src/app/models/wagon.model";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { RequestService } from "src/app/services/request.service";
import { FloorType } from "src/app/models/floortype.model";
import { FloorEnum } from "src/app/models/enums/FloorEnum";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { ClassAndEndpoint } from "src/app/models/classandendpoint.model";
import { take, takeUntil } from "rxjs/operators";
import { WaitForModalResult } from "src/app/helper/modalHelper";
import { MatDialog } from "@angular/material/dialog";
import { ModalDialog } from "src/app/Modal/modal";
import { ServiceClass } from "src/app/models/serviceClass.model";
import { changeServiceClassesOrder } from "src/app/helper/changeServiceClassesOrder";
import { dynamicLocaleCompareWithOptions } from "src/app/helper/sortFunctions";

@Component({
  selector: "app-wagon-edit",
  templateUrl: "./wagon-edit.component.html",
  styleUrls: ["./wagon-edit.component.css"],
})
export class WagonViewEditComponent implements OnInit, OnDestroy {
  // Properties
  projectId: number;
  wagonId: number;
  currentWagon: Wagon;
  wagons: Wagon[];
  selectedWagon;
  wagonForm;
  floorTypeList: Array<FloorType>;
  editMode: boolean;
  classEdit: boolean = false;
  wagonEdit: boolean = true;
  childValid: boolean = false;
  selectedFloor: number;
  classes: BehaviorSubject<Array<Array<ClassAndEndpoint>>> =
    new BehaviorSubject(null);
  private unscubscribe$ = new Subject<void>();
  serviceClasses: ServiceClass[] = null;
  skipCanDeactivateOnSave: boolean = false;
  backButtonModalMessage: string =
    "Please verify that you have saved the carriage, otherwise the unsaved changes will be lost!";
  private routeSub: Subscription;

  wagon = {
    addTitel: "Add New Carriage",
    editTitel: "Edit Carriage",
    chooseOtherWagonDescription: "Edit an existing carriage:",
    wagonSelectionLabel: "Choose carriage",
    wagonNameLabel: "Carriage name",
    wagonDescriptionLabel: "Carriage description",
    wagonLengthLabel:
      "Carriage length (dimensions of interiror space - Unit: mm)",
    wagonWidthLabel:
      "Carriage width (dimensions of interiror space - Unit: mm)",
    wagonFloorSelection: "Number of decks",
    buttonSave: "Save",
    buttonBack: "Back",
    classSelectionTitel: "Class selection",
    validation: {
      required: "This field is required!",
    },
    class: {
      createButton: "Create class definition",
      resetButton: "Reset class definition",
    },
  };

  // Ctor
  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private requestService: RequestService,
  ) {
    this.routeSub = this.route.params.subscribe((params) => {
      this.wagonId = parseInt(params["waggonId"].toString());
      this.selectedWagon = this.wagonId;
      this.projectId = parseInt(params["projectId"].toString());
      this.initForm();

      this.requestService.getMasterData()
        .pipe(takeUntil(this.unscubscribe$))
        .subscribe(masterData => {
          this.serviceClasses = changeServiceClassesOrder(masterData.serviceClassList);
        });

      this.loadWagons();
      this.getFloorTypes();

      if (this.wagonId > 0) {
        this.editMode = false;
        this.wagonEdit = false;
        this.loadWagon(this.wagonId);
      } else {
        this.editMode = true;
        this.wagonForm.get("xSize").enable();
        this.wagonForm.get("ySize").enable();
      }
    });
  }

  // Lifecycle hooks
  ngOnInit() { }
  getFloorTypes() {
    this.floorTypeList = new Array<FloorType>();

    let f1 = new FloorType();
    f1.id = 1;
    f1.name = 'Single deck vehicle';

    let f2 = new FloorType();
    f2.id = 2;
    f2.name = 'Double deck vehicle';

    this.floorTypeList.push(f1);
    this.floorTypeList.push(f2);
  }

  // Methods
  initForm() {
    let currentWagonId: number, currenProjectId: number;
    this.selectedWagon = null;
    currentWagonId = this.wagonId;
    currenProjectId = this.projectId;
    this.selectedFloor = FloorEnum.First;

    this.wagonForm = new FormGroup({
      id: new FormControl(currentWagonId),
      projectId: new FormControl(currenProjectId),
      name: new FormControl("", [Validators.required]),
      description: new FormControl(""),
      xSize: new FormControl({ value: 0, disabled: !this.editMode }, [
        Validators.required,
      ]),
      ySize: new FormControl({ value: 0, disabled: !this.editMode }, [
        Validators.required,
      ]),
      floorCount: new FormControl(this.selectedFloor, [Validators.required]),
      floors: new FormControl(null),
      classes: new FormControl(null, [Validators.required]),
    });
    this.classes.next(this.wagonForm.get("classes").value);
  }

  updateForm() {
    let wagonId,
      wagonName,
      wagonDescription,
      xSize,
      ySize,
      projectId,
      floorCount,
      floors,
      classes;
    if (this.currentWagon != null) {
      this.selectedWagon = this.currentWagon.id;
      wagonId = this.currentWagon.id;
      projectId = this.currentWagon.projectId;

      wagonName = this.currentWagon.name ? this.currentWagon.name : "";
      wagonDescription = this.currentWagon.description
        ? this.currentWagon.description
        : "";
      xSize = this.currentWagon.xSize ? this.currentWagon.xSize : 0;
      ySize = this.currentWagon.ySize ? this.currentWagon.ySize : 0;
      floorCount = this.currentWagon.floorCount;
      this.selectedFloor = floorCount;
      floors = this.currentWagon.floors;
      classes = this.currentWagon.classes;

      this.wagonForm = new FormGroup({
        id: new FormControl(wagonId),
        projectId: new FormControl(projectId),
        name: new FormControl(wagonName, [Validators.required]),
        description: new FormControl(wagonDescription),
        xSize: new FormControl({ value: xSize, disabled: !this.editMode }, [
          Validators.required,
        ]),
        ySize: new FormControl({ value: ySize, disabled: !this.editMode }, [
          Validators.required,
        ]),
        floorCount: new FormControl(
          { value: floorCount, disabled: !this.editMode },
          [Validators.required]
        ),
        floors: new FormControl(floors),
        classes: new FormControl(classes, [Validators.required]),
      });
      this.classes.next(this.wagonForm.get("classes").value);
    }
  }

  setClasses(event) {
    this.wagonForm.patchValue({
      classes: event,
    });
  }

  handleChildState(valid: boolean) {
    this.childValid = valid;
  }

  saveButtonDisabledState(): boolean {
    let disabled = this.childValid == false && this.wagonForm.invalid;
    return disabled;
  }

  createClassDefinitionButtonDisabledState() {
    let neccessaryInputsValid = this.wagonForm.get("xSize").value > 0 && this.wagonForm.get("ySize").value > 0 && this.wagonForm.get("floorCount").value > 0;

    if (neccessaryInputsValid) {
      return !this.wagonEdit;
    }

    return !neccessaryInputsValid;

  }

  loadWagons() {
    this.requestService
      .getWagons(this.projectId)
      .pipe(take(1))
      .subscribe((next) => {
        this.wagons = next.sort((a, b) =>
          dynamicLocaleCompareWithOptions(a, b, "name")
        );
      });
  }

  loadWagon(wagonId: number) {
    this.requestService
      .getWagon(wagonId)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe((next) => {
        this.currentWagon = next;
        this.updateForm();
      });
  }

  onSubmit() {
    this.skipCanDeactivateOnSave = true;
    let waggon = this.wagonForm.getRawValue();
    this.requestService
      .writeWagon(waggon)
      .pipe(take(1))
      .subscribe(
        (next) => {
          this.navigateBack();
        },
        (error) => {
          //
        }
      );
  }

  canDeactivate() {
    if (!this.skipCanDeactivateOnSave) {
      const dialogRef = this.dialog.open(ModalDialog, {
        data: {
          title: "Warning",
          message: this.backButtonModalMessage,
          showCancel: true,
          cancelButtonText: "Cancel",
          saveButtonText: "Leave",
        },
      });
      return WaitForModalResult(dialogRef);
    } else {
      return true;
    }
  }

  navigateBack() {
    this.router.navigate(["wagon/list/", this.projectId]);
  }

  changeWagon(selectedValue: number) {
    this.router.navigate(["wagon/edit/", this.projectId, selectedValue]);
  }

  setBoolValues(classEdit: boolean, wagonEdit: boolean) {
    this.classEdit = classEdit;
    this.wagonEdit = wagonEdit;
    const xSize = "xSize";
    const ySize = "ySize";
    const floorCount = "floorCount";

    if (wagonEdit) {
      this.wagonForm.get(xSize).enable();
      this.wagonForm.get(ySize).enable();
      this.wagonForm.get(floorCount).enable();
    } else {
      this.wagonForm.get(xSize).disable();
      this.wagonForm.get(ySize).disable();
      this.wagonForm.get(floorCount).disable();
    }
  }

  resetClasses() {
    this.wagonForm.patchValue({
      classes: null,
    });
  }

  changeFloor(selectedValue: number) {
    this.wagonForm.patchValue({
      floorCount: selectedValue,
    });
  }

  clearWagonDimension(dimension: string) {
    let id: string = dimension;
    if (this.getInput(id) == "0") {
      this.changeInput(id, null);
    }
  }

  addZeroToWagonDimension(dimension: string) {
    let id: string = dimension;
    if (this.getInput(id) == "") {
      this.changeInput(id, "0");
    }
  }

  changeInput(id: string, inputString: string) {
    (<HTMLInputElement>document.getElementById(id)).value = inputString;
  }

  getInput(id: string): string {
    return (<HTMLInputElement>document.getElementById(id)).value;
  }

  checkWagonDimensionIsMax(dimension: string, maxValue: number) {
    let wagonDimension: number = this.wagonForm.get(dimension).value;
    if (wagonDimension > maxValue) {
      this.showModal(
        "The maximum value of the carriage width is " + maxValue.toString(),
        dimension,
        maxValue
      );
    }
  }

  showModal(message: string, dimension: string, newValue: number): void {
    const dialogRef = this.dialog.open(ModalDialog, {
      data: {
        title: "Warning",
        message: message,
        showCancel: false,
        saveButtonText: "Okay",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.wagonForm.get(dimension).setValue(newValue);
      } else {
      }
    });
  }

  disableKeyPressE(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    //              e                  E
    if (charCode == 101 || charCode == 69) {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.unscubscribe$.next();
    this.unscubscribe$.complete();
    this.routeSub.unsubscribe();
  }
}
